import { Link, useParams } from 'react-router-dom'
import { AiFillHome } from 'react-icons/ai'
import {
  MdCheckCircle,
  MdOutlineKeyboardDoubleArrowRight,
} from 'react-icons/md'
import { useEffect, useState } from 'react'
import { fetchPractices, PracticeCategory } from '../../services/practices'
import {
  IoIosWarning,
  IoMdArrowDropleftCircle,
  IoMdArrowDroprightCircle,
  IoMdMail,
  IoMdRefresh,
} from 'react-icons/io'
import { FaPenFancy, FaSms, FaWhatsapp } from 'react-icons/fa'
import { FaLocationDot, FaPhone } from 'react-icons/fa6'
import { useTranslation } from 'react-i18next'
import { Libertin } from '../interfaces/libertin'
import { User, UserProfile, GalleryItem } from '../interfaces/user'
import { fetchAnnounceDetails } from '../../services/announcesLibertins'
import { fetchUsers } from '../../services/fetchAllUser'
import formatBelgianPhoneNumber from '../fonctions/formatBelgiumPhone'
import calculateAge from '../fonctions/calculateAge'
import formatTime from '../fonctions/formatTimeHourMinute'
import Map from '../components/Map'
import Tooltip from '../components/MyTooltip'
import Gallery from '../components/Gallery'
import { updateSearchFiltersAndRedirect } from '../fonctions/updateSearchFiltersAndRedirect'
import {
  hasPracticesWarnings,
  PracticesWarnings,
} from '../components/PracticesWarnings'
import SendMailModal from '../components/SendMailModal'
import ReportUserModal from '../components/ReportUserModal'

if (typeof global === 'undefined') {
  window.global = window
}

const dayMap: { [key: string]: number } = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 0,
}

function OneLibertin() {
  const { t } = useTranslation()
  const { id } = useParams<{ title: string; id: string }>()
  const [libertin, setLibertin] = useState<Libertin | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [gallery, setGallery] = useState<GalleryItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [practiceData, setPracticeData] = useState<PracticeCategory[]>([])
  const [, setSelectedProvinces] = useState<string[]>([])
  const [, setPracticeNames] = useState<string[]>([])
  const [isSendMailModalOpen, setIsSendMailModalOpen] = useState(false)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const formatURL = (title: string, id: number) => {
    return `${title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')}/${id}`
  }

  const getIdFromSlug = (slug: string | undefined): number | null => {
    if (!slug) return null
    try {
      const parts = slug.split('-')
      const encodedId = parts[parts.length - 1]

      const padding = '='.repeat((4 - (encodedId.length % 4)) % 4)
      const base64String = encodedId + padding

      const decodedId = atob(base64String)
      return parseInt(decodedId)
    } catch (error) {
      console.error('Error decoding slug:', error, 'Slug:', slug)
      return null
    }
  }

  const [tooltip, setTooltip] = useState<{
    province: string
    x: number
    y: number
  } | null>(null)

  const getPreviousSlug = () => {
    if (!libertin) return ''
    return formatURL(libertin.title, Number(libertin.id) - 1)
  }

  const getNextSlug = () => {
    if (!libertin) return ''
    return formatURL(libertin.title, Number(libertin.id) + 1)
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('Invalid libertin ID')
        return
      }

      try {
        const data = await fetchAnnounceDetails(id)
        if (data.libertin) {
          setLibertin(data.libertin)
          setGallery(data.gallery || [])

          const users = await fetchUsers()
          const foundUser = users.find(
            (u: User) => u.id === data.libertin.userId,
          )
          setUser(foundUser ?? null)
        } else {
          setError('Libertin not found')
        }
      } catch (error) {
        setError('Failed to fetch libertin details')
        console.error('Error fetching libertin details:', error)
      }
    }

    fetchData()
  }, [id])

  const onProvinceHover = (province: string, event: React.MouseEvent) => {
    const { clientX: x, clientY: y } = event
    setTooltip({ province, x, y })
  }

  const onMouseLeave = () => {
    setTooltip(null)
  }

  useEffect(() => {
    const getPractices = async () => {
      try {
        const data = await fetchPractices()
        setPracticeData(data)

        const practiceIds = libertin?.practices || []
        const names = practiceIds
          .map(id => {
            for (const category of data) {
              const practice = category.practices.find(
                practice => practice.id === id,
              )
              if (practice) {
                return practice.practiceName
              }
            }
            return null
          })
          .filter(name => name !== null)

        setPracticeNames(names)
      } catch (error) {
        console.error('Error fetching practices:', error)
      }
    }

    getPractices()
  }, [libertin])

  const handleLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault()
    if (user?.address?.city) {
      updateSearchFiltersAndRedirect(user.address.city)
    }
  }

  const userProfileFields = [
    { key: 'gender', label: 'create-ad.gender', usePrefix: true },
    { key: 'age', label: 'create-ad.age', usePrefix: false },
    {
      key: 'orientation',
      label: 'create-ad.sexual-orientation',
      usePrefix: true,
    },

    { key: 'size', label: 'create-ad.size', usePrefix: false },
    { key: 'weight', label: 'create-ad.weight', usePrefix: false },
    { key: 'hairColor', label: 'create-ad.hair', usePrefix: true },
    { key: 'eyeColor', label: 'create-ad.eyes', usePrefix: true },
    {
      key: 'nationality',
      label: 'create-ad.nationality',
      usePrefix: true,
      prefix: 'nationalities',
    },
    { key: 'ethnicity', label: 'create-ad.ethnicity', usePrefix: true },
    { key: 'braCup', label: 'create-ad.breasts', usePrefix: true },
    { key: 'penisSize', label: 'create-ad.penis-size', usePrefix: false },
    {
      key: 'intimateHairCut',
      label: 'create-ad.intimate-haircut',
      usePrefix: true,
    },
    { key: 'tatoo', label: 'create-ad.tatoo', usePrefix: true },
    { key: 'smoker', label: 'create-ad.smoker', usePrefix: true },
    { key: 'piercing', label: 'create-ad.piercings', usePrefix: true },
    { key: 'alcohol', label: 'create-ad.alcohol', usePrefix: true },
    {
      key: 'languages',
      label: 'create-ad.spoken-languages',
      usePrefix: true,
      prefix: 'languages',
    },
  ]

  const coverImage = gallery.find(item => item.cover === true)

  const isWithinTimeRange = (
    currentTime: string,
    start: string,
    end: string,
  ) => {
    const [currentHour, currentMin] = currentTime.split(':').map(Number)
    const [startHour, startMin] = start.split(':').map(Number)
    const [endHour, endMin] = end.split(':').map(Number)

    const current = currentHour * 60 + currentMin
    const startTime = startHour * 60 + startMin
    const endTime = endHour * 60 + endMin

    return current >= startTime && current <= endTime
  }
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  if (error) {
    return (
      <>
        <div className='bg-redpink'>
          <div className='max-w-7xl pb-2 mx-auto font-light'>
            <div className='flex pt-2'>
              <Link
                to='/'
                className='text-white hover:text-anthracite transition-all'
              >
                <AiFillHome className='text-2xl' />
              </Link>
              <MdOutlineKeyboardDoubleArrowRight className='mx-2 text-white text-2xl' />
              <span className='text-white'>{t('error')}</span>
            </div>
          </div>
        </div>
        <div className='max-w-7xl mx-auto py-10 text-center'>
          <div className='bg-grisclair p-8 rounded-lg shadow-md'>
            <IoIosWarning className='text-6xl text-redpink mx-auto mb-4' />
            <h2 className='text-2xl font-bold mb-4'>{t('error-occurred')}</h2>
            <p className='text-gray-600 mb-6'>{t('libertine-not-found')}</p>
            <div className='flex justify-center gap-4'>
              <Link
                to='/'
                className='bg-redpink text-white px-6 py-2 rounded hover:bg-anthracite transition-all inline-flex items-center'
              >
                <AiFillHome className='mr-2' />
                {t('dashboard.to-the-site')}
              </Link>
              <button
                onClick={() => window.location.reload()}
                className='bg-anthracite text-white px-6 py-2 rounded hover:bg-redpink transition-all inline-flex items-center'
              >
                <IoMdRefresh className='mr-2' />
                {t('reload-page')}
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className='bg-redpink'>
        <div className='max-w-7xl pb-2 mx-auto font-light flex justify-between '>
          <div className='flex pt-2'>
            <Link
              to='/'
              className='text-white hover:text-anthracite transition-all'
            >
              <AiFillHome className='text-2xl' />
            </Link>
            <MdOutlineKeyboardDoubleArrowRight className='mx-2 text-white text-2xl' />
            {Array.isArray(libertin?.status) &&
              libertin?.status.map((status, index) => (
                <div key={index} className='flex'>
                  <Link
                    to={`/${status}`}
                    className='text-white hover:text-anthracite transition-all'
                  >
                    {t(`create-ad.${status}`)}
                  </Link>
                  <MdOutlineKeyboardDoubleArrowRight className='mx-2 text-white text-2xl' />
                </div>
              ))}
            <p className='text-white'>{libertin?.title}</p>
          </div>
          <div className='flex'>
            {libertin && (
              <>
                <Link
                  to={`/libertin/${formatURL(
                    libertin.title,
                    Number(libertin.id) - 1,
                  )}`}
                >
                  <IoMdArrowDropleftCircle className='text-4xl hover:text-white transition-all' />
                </Link>
                <Link
                  to={`/libertin/${formatURL(
                    libertin.title,
                    Number(libertin.id) + 1,
                  )}`}
                >
                  <IoMdArrowDroprightCircle className='text-4xl hover:text-white transition-all' />
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      <div className='bg-anthracite'>
        <div className='max-w-7xl mx-auto font-light flex justify-between text-white py-3'>
          <div className='flex'>
            <FaPenFancy className='text-redpink text-xl mr-2' />
            {user && (
              <>
                {t('posted-by')}{' '}
                <Link
                  to={`/search/user/${user.id}`}
                  className='text-redpink pl-2 hover:underline transition-all'
                >
                  {user.nickname}
                </Link>
                {user.birthday && (
                  <span className='pl-2'>
                    ({calculateAge(user.birthday)} {t('years-old')})
                  </span>
                )}
                {libertin && (
                  <span className='pl-2'>
                    {t('at')} {formatTime(libertin.updatedAt)}
                  </span>
                )}
                <button
                  className='text-redpink flex hover:underline ml-5'
                  onClick={() => setIsReportModalOpen(true)}
                >
                  <IoIosWarning className='text-2xl mr-2' /> {t('report-user')}
                </button>
              </>
            )}
          </div>
          <div className='flex'>
            {libertin?.emailOnly && (
              <Link
                to='#'
                className='flex hover:underline ml-3'
                onClick={e => {
                  e.preventDefault()
                  if (user?.email) {
                    setIsSendMailModalOpen(true)
                  }
                }}
              >
                <IoMdMail className='text-blue-500 mr-2 text-2xl' />
                {t('send-mail')}
              </Link>
            )}
            <>
              <a
                href={`tel:${user?.phone}`}
                className='flex hover:underline ml-4'
              >
                <FaPhone className='text-emerald-500 mr-2 text-xl' />
                {formatBelgianPhoneNumber(user?.phone ?? '')}
              </a>
            </>
          </div>
        </div>
      </div>

      <div className='max-w-7xl mt-7 flex mx-auto mb-7'>
        {error ? (
          <p>{error}</p>
        ) : (
          libertin && (
            <>
              <div className='w-2/5'>
                <Gallery
                  gender={user?.userProfile.gender}
                  gallery={gallery}
                  coverImage={coverImage}
                  statusToShow={1}
                />
                {gallery.length > 1 && (
                  <div className='mt-4 flex flex-wrap gap-2 justify-between'>
                    {gallery.map((image, index) => (
                      <img
                        key={index}
                        src={image.fileUrl}
                        alt={`Image ${index + 1}`}
                        className='w-20 h-20 object-cover cursor-pointer rounded hover:opacity-80'
                        onClick={() => {
                          setCurrentImageIndex(index)
                          setIsGalleryModalOpen(true)
                        }}
                      />
                    ))}
                  </div>
                )}
                {libertin.escort && (
                  <>
                    <div className='text-lg font-light flex justify-center mt-5 bg-anthracite text-white rounded-t py-1'>
                      {t('leaving-from')}
                      <span className='text-redpink uppercase font-bold ml-2'>
                        {user?.address.city}
                      </span>
                    </div>
                    <div
                      className='relative bg-grisclair rounded-b'
                      onMouseLeave={onMouseLeave}
                    >
                      <Map
                        selectedProvinces={libertin.location}
                        onProvinceHover={onProvinceHover}
                        setSelectedProvinces={setSelectedProvinces}
                      />
                      {tooltip && <Tooltip province={tooltip.province} />}
                    </div>
                  </>
                )}
              </div>
              <div className='w-3/5 ml-7'>
                <p className='text-3xl'>{libertin.title}</p>
                <div className='flex'>
                  {libertin.status?.map((status, index) => (
                    <Link
                      key={index}
                      to={`/${status}/`}
                      className='flex items-center justify-center bg-redpink text-white px-2 py-0 mr-2 mt-1 rounded hover:bg-anthracite transition-all'
                    >
                      {t(`create-ad.${status}`)}
                    </Link>
                  ))}
                  <Link
                    className='flex bg-redpink text-white px-2 py-0 mr-2 mt-1 rounded hover:bg-anthracite transition-all'
                    to='/'
                    onClick={handleLinkClick}
                  >
                    <FaLocationDot className='mr-1 mt-1 text' />
                    {t(`${user?.address?.city}`)}
                  </Link>
                </div>

                <p className='my-5 whitespace-pre-line'>
                  {libertin.description}
                </p>
                <div className='border border-1 border-anthracite overflow-hidden rounded-lg'>
                  <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                    {t('contact')}
                  </p>
                  <div className='flex'>
                    <div
                      className={`${
                        libertin.isAvailable ||
                        (Array.isArray(libertin.disponibility) &&
                          libertin.disponibility.length > 0)
                          ? 'w-1/2'
                          : 'w-full'
                      }`}
                    >
                      <div className='flex hover:bg-grisclair justify-between py-2  px-4'>
                        <a
                          href={`tel:${user?.phone}`}
                          className='flex justify-between w-full'
                        >
                          <span className='flex items-center'>
                            <FaPhone className='text-redpink mr-2 text-xl' />
                            <span>{t('phone')}</span>
                          </span>
                          <span className='text-redpink'>
                            {formatBelgianPhoneNumber(user?.phone ?? '')}
                          </span>
                        </a>
                      </div>
                      {libertin?.whatsapp && (
                        <div className='flex hover:bg-grisclair justify-between py-2  px-4'>
                          <a
                            href={`https://wa.me/${user?.phone}`}
                            className='flex justify-between w-full'
                          >
                            <span className='flex items-center'>
                              <FaWhatsapp className='text-whatsapp text-xl mr-2' />
                              <span>WhatsApp</span>
                            </span>
                            <span className='text-redpink'>
                              {formatBelgianPhoneNumber(user?.phone ?? '')}
                            </span>
                          </a>
                        </div>
                      )}

                      {libertin.website && (
                        <p className='hover:bg-grisclair flex justify-between py-2 px-4'>
                          <span>{t('website')} :</span>
                          <Link
                            to={
                              libertin.website.startsWith('http')
                                ? libertin.website
                                : `http://${libertin.website}`
                            }
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-redpink'
                          >
                            {libertin.website}
                          </Link>
                        </p>
                      )}
                      {libertin.callOnly && (
                        <p className='hover:bg-grisclair flex justify-center py-2 px-4'>
                          <span>
                            <FaPhone className='mr-2' />
                          </span>
                          {t('create-ad.call-only')}
                        </p>
                      )}
                      {libertin.smsOnly && (
                        <p className='hover:bg-grisclair flex justify-center py-2 px-4'>
                          <span>
                            <FaSms className='mr-2' />
                          </span>
                          {t('create-ad.sms-only')}
                        </p>
                      )}
                    </div>
                    {(libertin.isAvailable ||
                      (Array.isArray(libertin.disponibility) &&
                        libertin.disponibility.length > 0)) && (
                      <div className='w-1/2'>
                        {libertin.isAvailable ? (
                          <p className='hover:bg-grisclair flex justify-between py-2  px-4'>
                            <span className='text-center w-full'>
                              {t('create-ad.available247')}
                            </span>
                          </p>
                        ) : (
                          Array.isArray(libertin.disponibility) &&
                          libertin?.disponibility?.map(
                            (day: string, index: number) => {
                              const currentDay = new Date().getDay()
                              const isDayToday =
                                dayMap[day.toLowerCase()] === currentDay
                              const isOpen =
                                libertin.availabilityStart?.[index] !==
                                  '00:00' &&
                                libertin.availabilityEnd?.[index] !== '00:00'

                              return (
                                <p
                                  key={index}
                                  className='flex justify-between py-2  px-4 hover:bg-grisclair'
                                >
                                  <span
                                    className={`flex items-center ${
                                      isDayToday ? 'font-bold' : ''
                                    }`}
                                  >
                                    <span>
                                      {t(`create-ad.${day.toLowerCase()}`)}
                                    </span>
                                  </span>
                                  <span
                                    className={`text-redpink ${
                                      isOpen ? '' : 'text-gray-400'
                                    }`}
                                  >
                                    {libertin.availabilityStart &&
                                    libertin.availabilityEnd &&
                                    libertin.availabilityStart[index] ===
                                      '00:00' &&
                                    libertin.availabilityEnd[index] ===
                                      '00:00' ? (
                                      t('24h/24')
                                    ) : (
                                      <span className='flex'>
                                        {isDayToday && (
                                          <span className='text-green-500 mr-2 mt-0.5'>
                                            {isWithinTimeRange(
                                              new Date().toLocaleTimeString(
                                                'en-US',
                                                {
                                                  hour: '2-digit',
                                                  minute: '2-digit',
                                                  hour12: false,
                                                },
                                              ),
                                              libertin.availabilityStart?.[
                                                index
                                              ] || '',
                                              libertin.availabilityEnd?.[
                                                index
                                              ] || '',
                                            ) && <MdCheckCircle />}
                                          </span>
                                        )}
                                        {libertin.availabilityStart?.[index] ??
                                          ''}
                                        <span className='px-1'>{t('to')}</span>
                                        {libertin.availabilityEnd?.[index] ??
                                          ''}
                                      </span>
                                    )}
                                  </span>
                                </p>
                              )
                            },
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className='border border-1 border-anthracite rounded-lg mt-5'>
                  <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                    {t('features')}
                  </p>
                  <div>
                    <div className='w-full grid grid-cols-3 text-center'>
                      {userProfileFields.map(field => {
                        const value =
                          user?.userProfile[field.key as keyof UserProfile]
                        return value && value !== '' ? (
                          <div
                            key={field.key}
                            className='w-full p-3 hover:bg-grisclair rounded-lg'
                          >
                            <p className='uppercase font-light'>
                              {t(field.label)}
                            </p>
                            <p className='font-semibold text-sm'>
                              {Array.isArray(value)
                                ? value
                                    .map(item =>
                                      t(
                                        `${
                                          field.prefix || 'create-ad'
                                        }.${item}`,
                                      ),
                                    )
                                    .join(', ')
                                : field.usePrefix
                                ? t(`${field.prefix || 'create-ad'}.${value}`)
                                : value}
                            </p>
                          </div>
                        ) : null
                      })}
                    </div>
                  </div>
                </div>
                {libertin?.practices && libertin.practices.length > 0 ? (
                  <div className='border mt-5 border-1 border-anthracite rounded-lg pb-2'>
                    <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                      {t('services')}
                    </p>
                    {practiceData.map(category => {
                      const filteredPractices = category.practices.filter(
                        practice => libertin?.practices.includes(practice.id),
                      )
                      return filteredPractices.length > 0 ? (
                        <div className='p-2 pb-0' key={category.id}>
                          <div className='w-full text-center bg-grisclair2 rounded-lg mb-2 uppercase p-1'>
                            {t(`create-ad.${category.categoryName}`)}
                          </div>
                          <div className='w-full grid grid-cols-3 text-center'>
                            {filteredPractices.map(practice => (
                              <div
                                className='relative px-2 py-3 text-sm font-semibold hover:bg-grisclair rounded-lg'
                                key={practice.id}
                              >
                                {t(`practices.${practice.practiceName}`)}
                                {hasPracticesWarnings(
                                  practice.practiceName,
                                ) && (
                                  <PracticesWarnings
                                    practiceName={practice.practiceName}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null
                    })}
                  </div>
                ) : null}
              </div>
            </>
          )
        )}
      </div>
      {tooltip && <Tooltip province={tooltip.province} />}
      {user?.email && (
        <SendMailModal
          isOpen={isSendMailModalOpen}
          onClose={() => setIsSendMailModalOpen(false)}
          recipientEmail={user.email}
          recipientName={user.nickname ?? ''}
        />
      )}
      {user && (
        <ReportUserModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          userNickname={user.nickname}
          userId={user.id}
        />
      )}
      {isGalleryModalOpen && (
        <div className='fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center'>
          <button
            className='absolute top-5 right-5 text-white text-3xl'
            onClick={() => setIsGalleryModalOpen(false)}
          >
            &times;
          </button>

          <button
            onClick={() =>
              setCurrentImageIndex(prev =>
                prev === 0 ? gallery.length - 1 : prev - 1,
              )
            }
            className='absolute left-4 text-white text-4xl'
          >
            &#10094;
          </button>

          <img
            src={gallery[currentImageIndex].fileUrl}
            alt={`Image ${currentImageIndex + 1}`}
            className='max-w-[90%] max-h-[90%] rounded-lg shadow-lg'
          />

          <button
            onClick={() =>
              setCurrentImageIndex(prev =>
                prev === gallery.length - 1 ? 0 : prev + 1,
              )
            }
            className='absolute right-4 text-white text-4xl'
          >
            &#10095;
          </button>
        </div>
      )}
    </>
  )
}

export default OneLibertin
